import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, getErrorMessage, put } from '../api/client';
import './SurveyAdmin.css';

export function SurveyAssignmentsView({
  roster,
  onAssign,
  savingId,
}) {
  const [query, setQuery] = useState('');
  const modules = roster?.modules || [];
  const closed = roster?.status === 'closed';
  const needle = query.trim().toLowerCase();
  const people = (roster?.people || []).filter((person) => {
    if (!needle) return true;
    return `${person.name} ${person.email}`.toLowerCase().includes(needle);
  });

  return (
    <div className="survey-admin">
      <PageHeader title="Survey assignments" section="leaders" />
      <p className="survey-admin-lead">
        Choose the modules each person should answer. You can change this while the survey is open.
      </p>
      <p className="survey-admin-links">
        <Link to="/admin/survey/progress">View progress</Link>
        {' · '}
        <Link to="/admin/survey/results">View results</Link>
      </p>
      {closed && (
        <p className="survey-admin-banner" role="status">
          This survey is closed. Assignments can&apos;t be changed.
        </p>
      )}
      <label className="survey-admin-search">
        <span>Search</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Name or email"
        />
      </label>
      {people.length === 0 && <p>No one matches that search.</p>}
      <ul className="survey-admin-list">
        {people.map((person) => (
          <li key={person.userId} className="survey-admin-card">
            <h2>{person.name}</h2>
            {person.email && <p className="survey-admin-email">{person.email}</p>}
            <fieldset disabled={closed || savingId === person.userId} className="survey-admin-modules">
              <legend>Modules</legend>
              {modules.map((mod) => {
                const checked = (person.assignedModules || []).includes(mod.key);
                return (
                  <label key={mod.key} className="survey-admin-module">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? person.assignedModules.filter((key) => key !== mod.key)
                          : [...(person.assignedModules || []), mod.key];
                        onAssign(person.userId, next);
                      }}
                    />
                    <span>{mod.key}. {mod.title}</span>
                  </label>
                );
              })}
            </fieldset>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SurveyAssignmentsPage = () => {
  const [roster, setRoster] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    get('/api/leaders-survey/roster')
      .then((response) => {
        if (cancelled) return;
        if (!response.ok) {
          setError(getErrorMessage(response, 'Failed to load people'));
          setLoading(false);
          return;
        }
        setRoster(response.data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load people');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onAssign = useCallback(async (userId, modules) => {
    const previous = roster;
    setRoster((current) => ({
      ...current,
      people: current.people.map((person) => (
        person.userId === userId ? { ...person, assignedModules: modules } : person
      )),
    }));
    setSavingId(userId);
    setError('');
    const response = await put('/api/leaders-survey/assignments', { userId, modules });
    setSavingId('');
    if (!response.ok) {
      setRoster(previous);
      setError(getErrorMessage(response, 'Failed to save assignment'));
      return;
    }
    setRoster((current) => ({
      ...current,
      people: current.people.map((person) => (
        person.userId === userId
          ? {
            ...person,
            assignedModules: response.data.assignedModules,
            modules: response.data.modules,
            overall: response.data.overall,
            lastActivityAt: response.data.lastActivityAt,
          }
          : person
      )),
    }));
  }, [roster]);

  if (loading) return <div className="survey-admin"><p>Loading…</p></div>;
  if (error && !roster) {
    return (
      <div className="survey-admin">
        <PageHeader title="Survey assignments" section="leaders" />
        <p className="survey-admin-error" role="alert">{error}</p>
      </div>
    );
  }

  return (
    <>
      {error && <p className="survey-admin survey-admin-error" role="alert">{error}</p>}
      <SurveyAssignmentsView roster={roster} onAssign={onAssign} savingId={savingId} />
    </>
  );
};

export default SurveyAssignmentsPage;
